import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AssetTable } from "@/components/v2/AssetTable";
import { ChartCard } from "@/components/v2/ChartCard";
import { EmptyState } from "@/components/v2/EmptyState";
import { InlineNativeSelect } from "@/components/v2/InlineNativeSelect";
import { SectionBlock } from "@/components/v2/SectionBlock";
import { StatGridCard } from "@/components/v2/StatGridCard";
import { PriceLineChart, ProviderBarMetricChart } from "@/components/v2/charts";
import { useMarketHistory } from "@/hooks/useHip3Analytics";
import { useNormalizedMarkets } from "@/hooks/useNormalizedMarkets";
import {
  aggregateAssetProviderComparisons,
  normalizeProviderMetricBars,
} from "@/lib/analytics-transforms";
import { formatCompact, formatFunding, formatSpreadBps } from "@/lib/formatters";
import { marketNotionalOi, normalizeAsset } from "@/lib/market-utils";

export function AssetDetailPage() {
  const params = useParams();
  const asset = normalizeAsset(params.asset ?? "");
  const [searchParams, setSearchParams] = useSearchParams();

  const marketsQuery = useNormalizedMarkets();
  const markets = useMemo(
    () => marketsQuery.markets.filter((market) => market.baseAsset === asset),
    [marketsQuery.markets, asset]
  );

  const providerPreferred = searchParams.get("provider")?.toLowerCase();

  const providerMarket = useMemo(() => {
    const byProvider = new Map<string, typeof markets[number]>();

    for (const market of markets) {
      const current = byProvider.get(market.provider);
      if (!current || (market.volume24h ?? 0) > (current.volume24h ?? 0)) {
        byProvider.set(market.provider, market);
      }
    }

    const options = Array.from(byProvider.values()).sort(
      (a, b) => (b.volume24h ?? 0) - (a.volume24h ?? 0)
    );

    const preferred = options.find((option) => option.provider === providerPreferred);
    return {
      selected: preferred ?? options[0],
      options,
    };
  }, [markets, providerPreferred]);

  const historyQuery = useMarketHistory({
    provider: providerMarket.selected?.provider,
    marketId: providerMarket.selected?.marketId,
    window: "30d",
    interval: "4h",
    enabled: Boolean(providerMarket.selected),
  });

  const stats = useMemo(() => {
    const oiUsd = markets.reduce((sum, market) => sum + marketNotionalOi(market), 0);
    const spreadValues = markets
      .map((market) => market.spreadBps)
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    const fundingValues = markets
      .map((market) => market.funding)
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

    return {
      totalOi: oiUsd,
      providerCount: new Set(markets.map((market) => market.provider)).size,
      marketCount: markets.length,
      fundingAvg:
        fundingValues.length > 0
          ? fundingValues.reduce((sum, value) => sum + value, 0) / fundingValues.length
          : null,
      spreadAvg:
        spreadValues.length > 0
          ? spreadValues.reduce((sum, value) => sum + value, 0) / spreadValues.length
          : null,
    };
  }, [markets]);

  const comparisonData = useMemo(
    () => aggregateAssetProviderComparisons(marketsQuery.markets, asset),
    [marketsQuery.markets, asset]
  );

  const pricePoints = useMemo(
    () =>
      historyQuery.data?.candles.map((candle) => ({
        t: candle.t,
        value: candle.close,
      })) ?? [],
    [historyQuery.data?.candles]
  );

  const oiBars = useMemo(
    () => normalizeProviderMetricBars(comparisonData.oiByProvider),
    [comparisonData.oiByProvider]
  );
  const spreadBars = useMemo(
    () => normalizeProviderMetricBars(comparisonData.spreadByProvider),
    [comparisonData.spreadByProvider]
  );
  const fundingBars = useMemo(
    () => normalizeProviderMetricBars(comparisonData.fundingByProvider),
    [comparisonData.fundingByProvider]
  );

  const hasPricePoints = pricePoints.some(
    (point) => typeof point.value === "number" && Number.isFinite(point.value)
  );

  if (!asset || markets.length === 0) {
    return (
      <EmptyState
        title="Asset not found"
        description="This asset has no markets in the HIP-3 directory."
      />
    );
  }

  return (
    <div className="space-y-6">
      <SectionBlock
        title={`${asset} Asset`}
        subtitle="Compare provider coverage, pricing, and execution quality for this asset."
      >
        <div className="grid gap-3 md:grid-cols-5">
          <StatGridCard label="Total OI" value={formatCompact(stats.totalOi, "$")} sub="Across providers" />
          <StatGridCard label="Providers" value={String(stats.providerCount)} sub="Active providers" />
          <StatGridCard label="Markets" value={String(stats.marketCount)} sub="Listed markets" />
          <StatGridCard label="Funding Avg" value={formatFunding(stats.fundingAvg)} sub="Average funding" />
          <StatGridCard label="Spread Avg" value={formatSpreadBps(stats.spreadAvg)} sub="Average spread" />
        </div>
      </SectionBlock>

      <SectionBlock
        title="Asset Charts"
        subtitle="Price and cross-provider comparison in one dense grid."
        actions={
          <InlineNativeSelect
            value={providerMarket.selected?.provider ?? ""}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              next.set("provider", event.target.value);
              setSearchParams(next, { replace: true });
            }}
            className="h-8 border border-border/70 bg-card pl-2 text-xs font-mono"
          >
            {providerMarket.options.map((option) => (
              <option key={`${option.provider}:${option.marketId}`} value={option.provider}>
                {option.provider.toUpperCase()} ({option.baseAsset}/{option.quoteAsset})
              </option>
            ))}
          </InlineNativeSelect>
        }
      >
        <div className="grid gap-3 md:grid-cols-2">
          <ChartCard
            title={`${asset} Price Over Time`}
            description={
              providerMarket.selected
                ? `Source: ${providerMarket.selected.provider.toUpperCase()}`
                : undefined
            }
            loading={historyQuery.isLoading}
          >
            {hasPricePoints ? (
              <PriceLineChart
                data={pricePoints}
                providerLabel={providerMarket.selected?.provider?.toUpperCase()}
              />
            ) : (
              <AssetChartMessage
                title="No price data available"
                description="Price history is unavailable for this provider and asset."
              />
            )}
          </ChartCard>
          <ChartCard title="Open Interest by Provider">
            {oiBars.length ? (
              <ProviderBarMetricChart
                data={oiBars}
                valueFormatter={(value) => formatCompact(value, "$")}
              />
            ) : (
              <AssetChartMessage
                title="No open interest comparison data"
                description="This asset does not have comparable open interest values across providers."
              />
            )}
          </ChartCard>
          <ChartCard title="Spread Comparison">
            {spreadBars.length ? (
              <ProviderBarMetricChart
                data={spreadBars}
                valueFormatter={(value) => `${value.toFixed(2)} bps`}
              />
            ) : (
              <AssetChartMessage
                title="No spread comparison data"
                description="Spread metrics are unavailable for this asset."
              />
            )}
          </ChartCard>
          <ChartCard title="Funding Comparison">
            {fundingBars.length ? (
              <ProviderBarMetricChart
                data={fundingBars}
                valueFormatter={(value) => formatFunding(value)}
              />
            ) : (
              <AssetChartMessage
                title="No funding comparison data"
                description="Funding metrics are unavailable for this asset."
              />
            )}
          </ChartCard>
        </div>
      </SectionBlock>

      <SectionBlock title="Asset Markets" subtitle="Every market listing for this asset.">
        <AssetTable markets={markets} />
      </SectionBlock>
    </div>
  );
}

function AssetChartMessage({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-[280px] w-full items-center justify-center border border-border/70 bg-background/45 px-4 text-center">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
